import closeIcon from '@jetbrains/icons/close.svg'
import alert from '@jetbrains/ring-ui/components/alert-service/alert-service'
import Confirm from '@jetbrains/ring-ui/components/confirm/confirm'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import * as React from 'react'
import type {SyntheticEvent} from 'react'
import {useMutation} from 'react-relay'

import IconButton from '../../../../../common/IconButton/IconButton'
import PopupMenu from '../../../../../common/Popup/PopupMenu'
import type {AgentPoolPageUnassignProjectFromAgentPoolMutation} from '../../__generated__/AgentPoolPageUnassignProjectFromAgentPoolMutation.graphql'
import {unassignProjectFromAgentPool} from '../../AgentPoolPage.queries'
import type {AgentPoolProjectsTreeProjectItem} from '../../AgentPoolPage.types'
import {stopPropagation} from '../../AgentPoolPage.utils'

import styles from './DiassociateProjectButton.css'

export function DiassociateProjectButton({
  agentPoolRawId,
  projectItem,
  onCompleted,
}: {
  agentPoolRawId: string
  projectItem: AgentPoolProjectsTreeProjectItem
  onCompleted: (projectItem: AgentPoolProjectsTreeProjectItem, recursively: boolean) => void
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false)
  const handleDeleteClick = React.useCallback(
    () => setShowDeleteConfirm(true),
    [setShowDeleteConfirm],
  )
  const [commitUnassign, isUnassignmentInFlight] =
    useMutation<AgentPoolPageUnassignProjectFromAgentPoolMutation>(unassignProjectFromAgentPool)

  const remove = React.useCallback(
    (recursively: boolean) => {
      commitUnassign({
        variables: {
          agentPoolRawId,
          projectRawId: projectItem.rawId,
          recursively,
        },
        onCompleted: (response, errors) => {
          if (errors == null) {
            onCompleted(projectItem, recursively)
          } else {
            errors.forEach(error => alert.error(error.message))
          }
        },
        onError: error => {
          alert.error(error.message)
        },
      })
    },
    [commitUnassign, agentPoolRawId, projectItem, onCompleted],
  )

  const onRemoveParent = React.useCallback(
    (item: unknown, e: Event | SyntheticEvent) => {
      e.stopPropagation()
      remove(false)
    },
    [remove],
  )

  const onRemoveParentRecursively = React.useCallback(
    (item: unknown, e: Event | SyntheticEvent) => {
      e.stopPropagation()
      remove(true)
    },
    [remove],
  )

  const onRemoveChild = React.useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation()
      remove(false)
    },
    [remove],
  )

  const onRemoveChildRecursively = React.useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation()
      remove(true)
      setShowDeleteConfirm(false)
    },
    [remove],
  )

  const onRejectDelete = React.useCallback(() => {
    setShowDeleteConfirm(false)
  }, [])

  if (isUnassignmentInFlight) {
    return <LoaderInline className={styles.loader} />
  } else if (projectItem.agentPools.length <= 1) {
    return (
      <>
        <IconButton
          icon={closeIcon}
          className={styles.unassignIcon}
          title="Dissociate"
          onClick={handleDeleteClick}
        />

        <Confirm
          text="Dissociate this project?"
          description={
            <>
              <p>
                {'You are about to dissociate\u00a0project\u00a0'}
                <strong>{`${projectItem?.name ?? ''}`}</strong>
                {'\u00a0from its last\u00a0pool '}
                <em>{`${projectItem?.agentPools[0].name}.`}</em>
              </p>
              <p>
                {
                  'This is the only\u00a0pool that is associated with this\u00a0project. If you dissociate it no builds of the\u00a0project can be started until you add it to another\u00a0pool.'
                }
              </p>
              <p>{'Are you sure you want to continue?'}</p>
            </>
          }
          confirmLabel="Yes"
          rejectLabel="Cancel"
          show={showDeleteConfirm}
          onConfirm={onRemoveChildRecursively}
          onReject={onRejectDelete}
        />
      </>
    )
  } else if (projectItem.children.length > 0) {
    return (
      <div onClick={stopPropagation} role="presentation">
        <Dropdown
          anchor={
            <IconButton icon={closeIcon} className={styles.unassignIcon} title="Dissociate" />
          }
        >
          <PopupMenu
            data={[
              {
                label: 'Unassign project with all subprojects',
                onClick: onRemoveParentRecursively,
              },
              {
                label: 'Unassign only this project',
                onClick: onRemoveParent,
              },
            ]}
          />
        </Dropdown>
      </div>
    )
  } else {
    return (
      <IconButton
        icon={closeIcon}
        className={styles.unassignIcon}
        title="Dissociate"
        onClick={onRemoveChild}
      />
    )
  }
}
